// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Badge {
  id: string;
  name: string;
  description: string;
  emoji: string;
  earned_at: string;
}

interface UseUserBadgesResult {
  badges: Badge[];
  loading: boolean;
  checkAndAwardBadges: (profileId: string) => Promise<string[]>;
}

export const useUserBadges = (profileId?: string): UseUserBadgesResult => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profileId) {
      setLoading(false);
      return;
    }
    
    const fetchBadges = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('wall_user_badges')
          .select(`
            badge_id,
            earned_at,
            wall_badges (
              id,
              name,
              description,
              emoji
            )
          `)
          .eq('profile_id', profileId)
          .order('earned_at', { ascending: false });

        if (error) {
          console.error('Error fetching badges:', error);
          return;
        }

        const formattedBadges: Badge[] = (data || []).map((item: any) => ({
          id: item.wall_badges.id,
          name: item.wall_badges.name,
          description: item.wall_badges.description,
          emoji: item.wall_badges.emoji,
          earned_at: item.earned_at
        }));

        setBadges(formattedBadges);
      } catch (err) {
        console.error('Error in useUserBadges:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBadges();
  }, [profileId]);

  const checkAndAwardBadges = async (targetProfileId: string): Promise<string[]> => {
    try {
      const { data, error } = await supabase.rpc('check_and_award_badges', {
        p_profile_id: targetProfileId
      });

      if (error) {
        console.error('Error checking badges:', error);
        return [];
      }

      const result = data as { success: boolean; badges_awarded: string[] };
      
      if (result.badges_awarded && result.badges_awarded.length > 0) {
        // Refetch badges to update display
        const { data: newBadges } = await supabase
          .from('wall_user_badges')
          .select(`
            badge_id,
            earned_at,
            wall_badges (
              id,
              name,
              description,
              emoji
            )
          `)
          .eq('profile_id', targetProfileId)
          .order('earned_at', { ascending: false });

        if (newBadges) {
          const formattedBadges: Badge[] = newBadges.map((item: any) => ({
            id: item.wall_badges.id,
            name: item.wall_badges.name,
            description: item.wall_badges.description,
            emoji: item.wall_badges.emoji,
            earned_at: item.earned_at
          }));
          setBadges(formattedBadges);
        }
      }

      return result.badges_awarded || [];
    } catch (err) {
      console.error('Error in checkAndAwardBadges:', err);
      return [];
    }
  };

  return { badges, loading, checkAndAwardBadges };
};
